import { createContext, useContext, useCallback } from 'react'
import { useMe, isResourceOfType, AllSchemaKeys } from '@opallabs/api'
import { useTrackActionMutation } from './useTrackActionMutation'

type TrackActionContextValue = {
  brand_id?: string
  brand_uuid?: string
  opal_id?: string
  locale: string
  'Browser Height': number
  'Browser Width': number
  Platform: 'Web'
  'Screen Height': number
  'Screen Width': number
}

/**
 * @see https://workwithopal.atlassian.net/wiki/spaces/ET/pages/3317596201/Segment+Event+Tracking
 */
export const TrackActionContext = createContext<
  TrackActionContextValue | undefined
>(undefined)

/**
 * @see https://workwithopal.atlassian.net/wiki/spaces/ET/pages/3317596201/Segment+Event+Tracking
 */
export const TrackActionProvider = ({
  children,
}: {
  children?: React.ReactNode
}) => {
  const { data } = useMe({
    select: (responseDocument) => {
      const momentSettings = responseDocument.included?.filter(
        isResourceOfType('moments_setting', 'V2')
      )[0]
      const currentWorkspace = responseDocument.included
        ?.filter(isResourceOfType('brand', 'V2'))
        .find(
          (brand) =>
            brand.id === momentSettings?.attributes.current_brand_id?.toString()
        )
      const opal = responseDocument.included?.filter(
        isResourceOfType('opal', 'V2')
      )[0]

      return {
        currentWorkspace: {
          id: currentWorkspace?.id,
          uuid: currentWorkspace?.attributes.uuid,
        },
        opalId: opal?.id,
      }
    },
  })

  return (
    <TrackActionContext.Provider
      value={{
        brand_id: data?.currentWorkspace.id,
        brand_uuid: data?.currentWorkspace?.uuid,
        opal_id: data?.opalId,
        locale: window.navigator.language,
        'Browser Height': window.innerHeight,
        'Browser Width': window.innerWidth,
        Platform: 'Web',
        'Screen Height': window.screen.height,
        'Screen Width': window.screen.width,
      }}
    >
      {children}
    </TrackActionContext.Provider>
  )
}

type ResourceObjectIds =
  | {
      [K in Exclude<AllSchemaKeys, 'brand' | 'opal'> as `${K}_id`]?: string
    }
  | undefined

type TrackObjectValue = {
  resourceObjectIds: ResourceObjectIds
}

/**
 * @see https://workwithopal.atlassian.net/wiki/spaces/ET/pages/3317596201/Segment+Event+Tracking
 */
export const TrackObjectContext = createContext<ResourceObjectIds>(undefined)

/**
 * Object ids to apply to all tracking events within the provider.
 *
 * @example
 * const resourceObjectIds = useMemo(() => ({ board_id: boardId }), [boardId])
 * //...
 * return (
 *   <TrackObjectProvider resourceObjectIds={resourceObjectIds}>
 *     // ...
 *   </TrackObjectProvider>
 * )
 *
 * @see https://workwithopal.atlassian.net/wiki/spaces/ET/pages/3317596201/Segment+Event+Tracking
 */
export const TrackObjectProvider: React.FC<
  React.PropsWithChildren<TrackObjectValue>
> = ({ children, resourceObjectIds }) => (
  <TrackObjectContext.Provider value={resourceObjectIds}>
    {children}
  </TrackObjectContext.Provider>
)

type PassedMetadata = Readonly<Record<string, unknown>>

/**
 * @see https://workwithopal.atlassian.net/wiki/spaces/ET/pages/3317596201/Segment+Event+Tracking
 */
export const useTrackAction = () => {
  const { mutateAsync: createTrackAction } = useTrackActionMutation()
  const defaultMetadata = useContext(TrackActionContext)
  const objectMetadata = useContext(TrackObjectContext)

  if (defaultMetadata === undefined) {
    throw new Error(
      'useTrackAction must be used within a TrackActionContextProvider.'
    )
  }
  const track = useCallback(
    /**
     * Event name to track, plus any metadata that's not already set by
     * `<TrackActionprovider>` or `<TrackObjectProvider>`.
     *
     * @see https://workwithopal.atlassian.net/wiki/spaces/ET/pages/3317596201/Segment+Event+Tracking
     */
    async (eventName: string, metadata: PassedMetadata = {}) => {
      await createTrackAction({
        event: eventName,
        metadata: {
          ...defaultMetadata,
          ...objectMetadata,
          ...metadata,
          title: window.top?.document.title,
          url: window.location.toString(),
        },
      })
    },
    [createTrackAction, defaultMetadata, objectMetadata]
  )

  return track
}
